import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { contractUnitOptionProps } from '../../../../@core/utils/helper';
import { getContractTermPresetButtons } from '../../../custom-formly/contract-term-presets';
import { BILLING_PERIOD_SPAN_CLASS } from '../formly-billing-period-span/formly-billing-period-span.component';

type LineItemDetailFormState = {
    detailComponent?: {
        onContractFormChanged?: () => void;
    };
    showContractLineItemFields?: boolean;
};

function contractFormChangeHooks(): NonNullable<FormlyFieldConfig['hooks']> {
    return {
        onInit(field) {
            const contractGroup = field.form?.get('contractDocumentLineItem');
            const notify = () => {
                const formState = field.options?.formState as LineItemDetailFormState | undefined;
                formState?.detailComponent?.onContractFormChanged?.();
            };
            const subs: Subscription[] = [];
            if (contractGroup) {
                subs.push(contractGroup.valueChanges.subscribe(() => notify()));
            }
            for (const key of ['serviceRenderedStartDate', 'serviceRenderedEndDate'] as const) {
                const control = field.form?.get(key);
                if (control) {
                    subs.push(control.valueChanges.subscribe(() => notify()));
                }
            }
            (field as FormlyFieldConfig & { __contractFormSub?: Subscription[] }).__contractFormSub = subs;
        },
        onDestroy(field) {
            const f = field as FormlyFieldConfig & { __contractFormSub?: Subscription[] };
            f.__contractFormSub?.forEach((sub) => sub.unsubscribe());
            f.__contractFormSub = undefined;
        },
    };
}

/** Contract tab for document line item detail (contract documents and contract targets). */
export function getContractLineItemTabGroup(): FormlyFieldConfig {
    return {
        expressions: {
            hide: '!formState.showContractLineItemFields',
        },
        validators: {
            validation: ['contractFactorTerm'],
        },
        fieldGroupClassName: 'flex flex-wrap',
        props: {
            label: _('document-line-item.contract'),
            icon: 'fa fa-repeat',
            translate: true,
            attributes: {
                'data-testid': 'formly-dli-contract',
            },
        },
        fieldGroup: [
            {
                fieldGroupClassName: 'flex flex-wrap',
                className: 'w-full',
                hooks: contractFormChangeHooks(),
                fieldGroup: [
                    {
                        ...getContractTermPresetButtons('contractDocumentLineItem', 'formly-dli-contract-preset'),
                        // Hidden alongside the cycle fields: on a contract target only the factor half
                        // of the term is editable, which no preset can fill on its own.
                        expressions: {
                            hide: '!!formState.document?.documentDefinition?.canBeContractTarget',
                        },
                    },
                    {
                        className: 'w-full md:w-1/2',
                        fieldGroupClassName: 'flex flex-wrap',
                        fieldGroup: [
                            {
                                key: 'contractDocumentLineItem.cycleInterval',
                                className: 'w-full md:w-1/2',
                                type: 'input',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.cycleInterval'),
                                    translate: true,
                                    type: 'number',
                                    min: 1,
                                    attributes: {
                                        'data-testid': 'formly-dli-cycle-interval-input',
                                    },
                                },
                                expressions: {
                                    hide: '!!formState.document?.documentDefinition?.canBeContractTarget',
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.cycleUnit',
                                className: 'w-full md:w-1/2',
                                type: 'formly-generic-enum-drop-down',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.cycleUnit'),
                                    translate: true,
                                    ...contractUnitOptionProps('contractDocumentLineItem', 'cycleUnit'),
                                    attributes: {
                                        enumName: 'DocumentLineItemBillingPeriodUnit',
                                        'data-testid': 'formly-dli-cycle-unit-drop-down',
                                    },
                                },
                                expressions: {
                                    hide: '!!formState.document?.documentDefinition?.canBeContractTarget',
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.contractFactor',
                                className: 'w-full md:w-1/2',
                                type: 'input',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.contractFactor'),
                                    translate: true,
                                    type: 'number',
                                    min: 1,
                                    attributes: {
                                        'data-testid': 'formly-contract-factor-input',
                                    },
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.contractFactorUnit',
                                className: 'w-full md:w-1/2',
                                type: 'formly-generic-enum-drop-down',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.contractFactorUnit'),
                                    translate: true,
                                    ...contractUnitOptionProps('contractDocumentLineItem', 'contractFactorUnit'),
                                    attributes: {
                                        enumName: 'DocumentLineItemBillingPeriodUnit',
                                        'data-testid': 'formly-dli-contract-period-unit-drop-down',
                                    },
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.preDurationMinutes',
                                className: 'w-full md:w-1/2',
                                type: 'input',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.preDurationMinutes'),
                                    translate: true,
                                    type: 'number',
                                    min: 0,
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.postDurationMinutes',
                                className: 'w-full md:w-1/2',
                                type: 'input',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.postDurationMinutes'),
                                    translate: true,
                                    type: 'number',
                                    min: 0,
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.generateDocument',
                                className: 'w-full',
                                type: 'formly-document-definition-dropdown',
                                wrappers: ['simple-title'],
                                props: {
                                    label: _('document-line-item.generateDocument'),
                                    translate: true,
                                    onlyCanBeContractTarget: true,
                                    attributes: {
                                        'data-testid': 'formly-dli-contract-generate-document-dropdown',
                                    },
                                },
                                expressions: {
                                    hide: `!(
                                        formState.document?.documentDefinition?.documentType === 'CONTRACT'
                                        || formState.document?.documentDefinition?.documentType === 'Contract'
                                        || formState.document?.documentDefinition?.identifier === 'CO'
                                        || formState.document?.documentDefinition?.identifier === 'ICO'
                                        || formState.document?.documentDefinitionIdentifier === 'CO'
                                        || formState.document?.documentDefinitionIdentifier === 'ICO'
                                    )`,
                                    'props.docDirection': 'formState.document?.documentDefinition?.documentDirection',
                                    // Prefer parent contract Belegart; only fall back to INV/INI when unset on the document.
                                    'props.defaultValue':
                                        `formState.document?.contractDocument?.generateDocument?.identifier`
                                        + ` || (formState.document?.documentDefinition?.documentDirection === 'IN' ? 'INI' : 'INV')`,
                                },
                            },
                        ],
                    },
                    {
                        className: 'w-full md:w-1/2',
                        fieldGroupClassName: 'flex flex-wrap',
                        fieldGroup: [
                            {
                                key: 'contractDocumentLineItem.billingPeriodFrom',
                                className: 'w-full md:w-1/2',
                                type: 'formly-date-picker',
                                wrappers: ['simple-title'],
                                props: {
                                    translate: true,
                                    label: 'document-line-item.billingPeriodFrom',
                                    showTime: false,
                                },
                            },
                            {
                                key: 'contractDocumentLineItem.billingPeriodTo',
                                className: 'w-full md:w-1/2',
                                type: 'formly-date-picker',
                                wrappers: ['simple-title'],
                                props: {
                                    translate: true,
                                    label: 'document-line-item.billingPeriodTo',
                                    showTime: false,
                                },
                            },
                            {
                                type: 'formly-billing-period-span',
                                className: BILLING_PERIOD_SPAN_CLASS,
                            },
                            {
                                wrappers: ['accordion'],
                                className: 'w-full',
                                fieldGroupClassName: 'flex flex-wrap',
                                props: {
                                    label: _('document-line-item.billingPeriodTimesAccordion'),
                                    translate: true,
                                    attributes: {
                                        activeIndex: -1,
                                        'data-testid': 'formly-dli-billing-period-times-accordion',
                                    },
                                },
                                fieldGroup: [
                                    {
                                        key: 'contractDocumentLineItem.billingPeriodFromTime',
                                        className: 'w-full md:w-1/2',
                                        type: 'formly-date-picker',
                                        wrappers: ['simple-title'],
                                        props: {
                                            label: _('document-line-item.billingPeriodFromTime'),
                                            translate: true,
                                            timeOnly: true,
                                            attributes: {
                                                'data-testid': 'formly-dli-billing-period-from-time',
                                            },
                                        },
                                    },
                                    {
                                        key: 'contractDocumentLineItem.billingPeriodToTime',
                                        className: 'w-full md:w-1/2',
                                        type: 'formly-date-picker',
                                        wrappers: ['simple-title'],
                                        props: {
                                            label: _('document-line-item.billingPeriodToTime'),
                                            translate: true,
                                            timeOnly: true,
                                            attributes: {
                                                'data-testid': 'formly-dli-billing-period-to-time',
                                            },
                                        },
                                    },
                                ],
                            },
                            {
                                key: 'serviceRenderedStartDate',
                                className: 'w-full md:w-1/2',
                                type: 'formly-date-picker',
                                wrappers: ['simple-title'],
                                // hidden until the service-rendered date logic is reworked
                                hide: true,
                                resetOnHide: false,
                                props: {
                                    translate: true,
                                    label: 'document.service-rendered-start-date',
                                    showTime: false,
                                    attributes: {
                                        'data-testid': 'formly-dli-service-rendered-start-date',
                                    },
                                },
                            },
                            {
                                key: 'serviceRenderedEndDate',
                                className: 'w-full md:w-1/2',
                                type: 'formly-date-picker',
                                wrappers: ['simple-title'],
                                hide: true,
                                resetOnHide: false,
                                props: {
                                    translate: true,
                                    label: 'document.service-rendered-end-date',
                                    showTime: false,
                                    attributes: {
                                        'data-testid': 'formly-dli-service-rendered-end-date',
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    };
}
